import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';

import IconButton from '@material-ui/core/IconButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import type OrderItem from '@this/src/domain/order_item2';
import type TransportElement from '@this/src/domain/transport_element2';
import type TravelerInformation from '@this/src/domain/traveler_information';

interface Props {
  orderItem: OrderItem;
  element: TransportElement;
  elementIndex: number;
}

const getIsShinkansen = (element: TransportElement) => {
  return element.isShinkansen || element.isExpress || element.isForeignRailway;
};

const getIsBusinessEkinet = (element: TransportElement, orderItem: OrderItem, elementIndex: number) => {
  if (element.railwayTicketType === 4) return 'all';
  if (orderItem.travelerInformations.length < 1) return false;

  const travelerSome = orderItem.travelerInformations.some(traveler => {
    if (
      traveler.travelerInformationShinkansenTypes &&
      traveler.travelerInformationShinkansenTypes.length > 0 &&
      traveler.travelerInformationShinkansenTypes[elementIndex]
    ) {
      return traveler.travelerInformationShinkansenTypes[elementIndex].shinkansenTicketType === 'business_ekinet';
    }
    return traveler.shinkansenTicketType === 'business_ekinet';
  });
  if (travelerSome) return 'part';

  return false;
};

const getTravelers = (orderItem: OrderItem, isBusinessEkinet: 'all' | 'part' | false, elementIndex: number) => {
  if (isBusinessEkinet === 'all') return orderItem.travelerInformations;
  if (isBusinessEkinet === 'part') {
    return orderItem.travelerInformations.filter(traveler => {
      if (traveler.travelerInformationShinkansenTypes && traveler.travelerInformationShinkansenTypes.length > 0) {
        return (
          traveler.travelerInformationShinkansenTypes[elementIndex]?.shinkansenTicketType === 'business_ekinet'
        );
      }
      return traveler.shinkansenTicketType === 'business_ekinet';
    });
  }
  return [];
};

export const UserBusinessEkinetAccount: React.FC<Props> = observer(({ orderItem, element, elementIndex }) => {
  const [openIds, setOpenIds] = React.useState<number[]>([]);
  const isShinkansen = useMemo(() => getIsShinkansen(element), [element]);
  const isBusinessEkinet = useMemo(
    () => getIsBusinessEkinet(element, orderItem, elementIndex),
    [element, orderItem.travelerInformations, elementIndex]
  );
  const travelers = useMemo(
    () => getTravelers(orderItem, isBusinessEkinet, elementIndex),
    [isBusinessEkinet, orderItem.travelerInformations, elementIndex]
  );

  const handleToggleOpen = useCallback((id: number) => {
    setOpenIds(current => {
      if (current.includes(id)) {
        return current.filter(openId => openId !== id);
      }
      return [...current, id];
    });
  }, []);

  const renderPassword = (password: string | undefined, id: number) => {
    if (password) {
      return (
        <p>
          ビジネスえきねっとパスワード：
          {openIds.includes(id) ? password : password.replace(/./g, '*')}
          <IconButton size="small" onClick={() => handleToggleOpen(id)}>
            {openIds.includes(id) ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </p>
      );
    }
    return <p>ビジネスえきねっとパスワード：未設定</p>;
  };

  const renderAccountInfo = (ti: TravelerInformation) => {
    const account = ti.user?.businessEkinetAccount;
    return (
      <>
        <p>ビジネスえきねっと会員ID：{account?.businessEkinetId || '未設定'}</p>
        {renderPassword(account?.password, ti.id ?? 0)}
      </>
    );
  };

  if (!isShinkansen || !isBusinessEkinet || travelers.length < 1) return null;

  return (
    <div data-testid="latest-business-ekinet-item-display">
      ■ ビジネスえきねっと（最新の情報）
      <br />
      {travelers.map(ti => (
        <div key={ti.id}>
          {ti.lastNameKana} {ti.firstNameKana}：
          <br />
          <div style={{ marginLeft: '10px' }}>{renderAccountInfo(ti)}</div>
        </div>
      ))}
    </div>
  );
});
